import { useMemo } from "react";
import { useQueries } from "@tanstack/react-query";
import { useApiClient } from "../../api/ApiProvider";
import { queryKeys, useProjects } from "../../api/queries";
import type { Project, Service } from "../../api/types";

/**
 * `GET /nodes/{nodeId}/placements` (and the equivalent host-scoped read)
 * returns bare `Placement`s (`serviceId` only, no `projectId` — see the wire
 * shape in server/internal/projectsvc/placement_http.go) because a
 * placement's own record never carried project context to begin with
 * (ADR-0025 §2: `UNIQUE(service_id, node_id)`, nothing project-scoped).
 * Resolving "which service, in which project" for a node/host-centric view
 * therefore means cross-referencing every project's services client-side —
 * there is no fleet-wide services list or per-id service lookup without a
 * project id in hand. Bounded the same way every reference list in this
 * codebase is (limit: 100, matching the `nodesById` precedent in
 * PlacementsSection): fine for the realistic project/service counts through
 * Beta Phase 1, but a fleet-wide "placements with service+project attached"
 * read model would remove this N+1 if the counts grow.
 *
 * Extracted (charter A-12) once the same resolution recurred a third time
 * across the node-centric and host-centric detail views (both now unified
 * into `HostDetailPage`) and the fleet-wide routes view (`useRouteRows`) —
 * lifted here so none of them can maintain their own N+1 fan-out that could
 * quietly diverge.
 */
export function useServiceProjectIndex() {
  const client = useApiClient();
  const { data: projectsPage, isPending: projectsPending, isError: projectsIsError, error: projectsError } =
    useProjects({ limit: 100 });
  const projects = useMemo(() => projectsPage?.items ?? [], [projectsPage]);

  const serviceQueries = useQueries({
    queries: projects.map((project) => ({
      queryKey: queryKeys.services(project.id),
      queryFn: () => client.listServices({ projectId: project.id, limit: 100 }),
      enabled: Boolean(project.id),
    })),
  });

  const isPending = projectsPending || serviceQueries.some((q) => q.isPending);
  const isError = projectsIsError || serviceQueries.some((q) => q.isError);
  const error = projectsError ?? serviceQueries.find((q) => q.error)?.error;

  const index = useMemo(() => {
    const byServiceId = new Map<string, { service: Service; project: Project }>();
    projects.forEach((project, i) => {
      const services = serviceQueries[i]?.data?.items ?? [];
      services.forEach((service) => byServiceId.set(service.id, { service, project }));
    });
    return byServiceId;
  }, [projects, serviceQueries]);

  return { index, isPending, isError, error };
}
